import { defineStore } from "pinia";
import { useConversationStore } from "./useConversationStore";
import { DraftConvEnum } from "../../../enums/DraftConvEnums";
import { useUserStore } from "./useUsersStore";

export const useDraftsStore = defineStore("drafts", {
    state() {
        return {
            messageDrafts: {} as Record<string, any>,
            conversationDrafts: {} as Record<string, any>
        };
    },

    getters: {
        getDraftMessage: (state) => {
            return (convId: string) => {
                return state.messageDrafts[convId];
            };
        },

        getAllConversationDrafts: (state) => {
            return Object.values(state.conversationDrafts);
        }
    },

    actions: {
        loadDrafts() {
            this.conversationDrafts = JSON.parse(window.sessionStorage.getItem("draftConversations")) ?? {};
            this.messageDrafts = JSON.parse(window.sessionStorage.getItem("draftMessages")) ?? {};

            // if draft convs -> upsert users
            const conversations = Object.values(this.conversationDrafts);
            if (conversations.length > 0) {
                const userStore = useUserStore();
                conversations.forEach((conv) => {
                    userStore.upsertUsers(conv.users);
                });
            }
        },

        saveDraftMessage(draftId: string, draftdetails: any) {
            this.messageDrafts[draftId] = draftdetails;

            const convStore = useConversationStore();
            convStore.updateConvDetails(draftId, { ...convStore.conversations[draftId], hasDraftMessage: true });

            window.sessionStorage.setItem("draftMessages", JSON.stringify(this.messageDrafts));
        },

        removeDraftMessage(draftId: string) {
            if (this.messageDrafts[draftId]) {
                delete this.messageDrafts[draftId];
                window.sessionStorage.setItem("draftMessages", JSON.stringify(this.messageDrafts));
                const convStore = useConversationStore();
                convStore.updateConvDetails(draftId, { ...convStore.conversations[draftId], hasDraftMessage: false });
            }
        },

        createDraftConversation(draftId: string, convDetails: any, msg: any) {
            this.conversationDrafts[draftId] = convDetails;
            if (msg) {
                this.messageDrafts[draftId] = msg;
                window.sessionStorage.setItem("draftMessages", JSON.stringify(this.messageDrafts));
            }
            window.sessionStorage.setItem("draftConversations", JSON.stringify(this.conversationDrafts));
        },

        updateDraftConversation(draftDetails) {
            switch (draftDetails.actionType) {
                case DraftConvEnum.CreateConv:
                    delete this.conversationDrafts[draftDetails.id];
                    this.removeDraftMessage(draftDetails.id);
                    break;
                case DraftConvEnum.EditTitle:
                    this.conversationDrafts[draftDetails.id].title = draftDetails.newTitle;
                    break;
                case DraftConvEnum.AddParticipant:
                    this.conversationDrafts[draftDetails.id].users = [
                        ...this.conversationDrafts[draftDetails.id].users,
                        ...draftDetails.users
                    ];
                    this.conversationDrafts[draftDetails.id].loginIds = [
                        ...this.conversationDrafts[draftDetails.id].loginIds,
                        ...draftDetails.users.map((user) => user.loginId)
                    ];
                    break;
                case DraftConvEnum.RemoveParticipant:
                    const updatedUsers = this.conversationDrafts[draftDetails.id].loginIds.filter(
                        (id: string) => id !== draftDetails.participantId
                    );
                    this.conversationDrafts[draftDetails.id].loginIds = updatedUsers;
                    break;
                case DraftConvEnum.DeleteConv:
                    const convStore = useConversationStore();
                    let draftIds = Object.keys(this.conversationDrafts);
                    const index = draftIds.findIndex((id: string) => id === draftDetails.id);
                    if (index < draftIds.length - 1) {
                        convStore.setActiveConversationId(draftIds[index + 1]);
                    } else convStore.setActiveConversationId(convStore.orderedConvIds[0]);
                    delete this.conversationDrafts[draftDetails.id];
                    break;
            }
            window.sessionStorage.setItem("draftConversations", JSON.stringify(this.conversationDrafts));
        },

        removeDraftConversations(draftId: string) {
            delete this.conversationDrafts[draftId];
        }
    }
});
